<script setup lang="ts">
import { lectureService } from '@/api/services/lecture-service'
import ChevronDown from '@/assets/svgs/chevron_down.svg'
import ChevronUp from '@/assets/svgs/chevron_up.svg'
import HeartIcon from '@/assets/svgs/heart.svg'
import HeartFilledIcon from '@/assets/svgs/heart_fill.svg'
import { useIsMobile } from '@/composables/useMediaQuery'
import type { LectureDetail } from '@/types/lecture.types'
import { getKoreanLevelFromLecture } from '@/utils/lectureLevel'
import { computed, ref } from 'vue'

const props = defineProps<{
  lecture: LectureDetail
}>()

const isMobile = useIsMobile()

const isLiked = ref(props.lecture.bookmarked)
const openMentorInfo = ref(false)

const computedMentorInfo = computed(() => {
  return !isMobile.value || openMentorInfo.value
})

const koreanLevel = getKoreanLevelFromLecture(props.lecture)

const toggleMentorInfo = () => {
  openMentorInfo.value = !openMentorInfo.value
}

const toggleLike = async (event: Event) => {
  event.stopPropagation()
  const isBookmarked = await lectureService.toggleBookmark(props.lecture.idx)
  isLiked.value = isBookmarked
}

const computedHeartSvg = computed(() => {
  return isLiked.value ? HeartFilledIcon : HeartIcon
})
</script>

<template>
  <div class="video-info-section">
    <div class="video-title-header">
      <div class="video-title-header-title title-md white">{{ lecture.name }}</div>
      <div class="video-title-header-heart cursor-pointer">
        <img
          @click="toggleLike"
          :class="{ 'svg-white': !isLiked }"
          class="svg-24"
          :src="computedHeartSvg"
          alt="heart"
        />
      </div>
    </div>
    <div class="video-info-section-content">
      <div class="video-info-section-content-mentor">
        <PerfectScrollbar :options="{ wheelSpeed: 0.5 }">
          <div class="video-info-section-content-mentor-title-header">
            <div
              class="video-info-section-content-mentor-title title-sm white"
              :class="{ 'body-lg': isMobile }"
            >
              멘토 소개
            </div>
            <img
              @click="toggleMentorInfo"
              v-if="isMobile"
              class="svg-24 cursor-pointer"
              :src="openMentorInfo ? ChevronUp : ChevronDown"
              alt="chevron_down"
            />
          </div>
          <div v-if="computedMentorInfo" class="video-info-section-content-mentor-info">
            <div class="video-info-section-content-mentor-avatar">
              <img :src="lecture.mentorSquareThumbnailUrl" alt="mentor avatar" />
            </div>
            <div class="video-info-section-content-mentor-name body-lg white">
              {{ lecture.mentorName }}
            </div>
            <div
              class="video-info-section-content-mentor-description body-md white"
              :class="{ 'body-sm': isMobile }"
            >
              {{ lecture.mentorDescription }}
            </div>
          </div>
        </PerfectScrollbar>
      </div>

      <div class="video-info-section-content-description">
        <PerfectScrollbar :options="{ wheelSpeed: 0.5 }">
          <div
            class="video-info-section-content-description-title title-sm white"
            :class="{ 'body-lg': isMobile }"
          >
            강좌설명
          </div>
          <div
            class="video-info-section-content-description-text body-md white"
            :class="{ 'body-sm': isMobile }"
          >
            {{ lecture.description }}
          </div>
          <div class="video-info-section-content-description-info">
            <div class="video-info-section-content-description-info-title title-sm white">정보</div>
            <div
              class="video-info-section-content-description-info-text body-md white"
              :class="{ 'body-sm': isMobile }"
            >
              레벨 : {{ koreanLevel }}
            </div>
            <div
              class="video-info-section-content-description-info-text body-md white"
              :class="{ 'body-sm': isMobile }"
            >
              뮤직 : {{ lecture.music }}
            </div>
          </div>
        </PerfectScrollbar>
      </div>
    </div>
  </div>
</template>

<style scoped>
.video-title-header {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
}

.video-info-section-content {
  display: flex;
  flex-direction: row;
  align-items: start;
  gap: 20px;
  margin-top: 32px;
}

.video-info-section-content-mentor {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: start;
  padding: 20px;
  border-radius: 8px;
  background-color: var(--gray-800);
  min-height: 300px;
  max-height: 300px;
  overflow-y: auto;
  width: 630px;
  min-width: 630px;
  box-sizing: border-box;
}

.video-info-section-content-mentor-title-header {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  box-sizing: border-box;
}

.video-info-section-content-mentor-avatar {
  width: 80px;
  height: 80px;
  border-radius: 100%;
  margin-top: 12px;
  width: 100%;
  box-sizing: border-box;
}

.video-info-section-content-mentor-avatar img {
  height: 100%;
  border-radius: 100%;
  object-fit: cover;
  object-position: center;
}

.video-info-section-content-mentor-name {
  margin-top: 12px;
  width: 100%;
  box-sizing: border-box;
}

.video-info-section-content-mentor-description {
  margin-top: 8px;
  width: 100%;
  box-sizing: border-box;
}

.video-info-section-content-description {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: start;
  padding: 20px;
  border-radius: 8px;
  background-color: var(--gray-800);
  min-height: 300px;
  max-height: 300px;
  overflow-y: auto;
  width: 630px;
  min-width: 630px;
  box-sizing: border-box;
}

.video-info-section-content-description-text {
  margin-top: 12px;
  white-space: pre-line;
}

.video-info-section-content-description-info {
  margin-top: 32px;
  display: flex;
  flex-direction: column;
  align-items: start;
  gap: 12px;
}

.video-info-section-content-description-info-title {
  width: 100%;
  box-sizing: border-box;
}

.video-info-section-content-description-info-text {
  width: 100%;
  box-sizing: border-box;
}

.ps {
  width: 100%;
}

.ps__rail-y {
  right: 0;
}

@media (max-width: 1365px) {
  .video-title-header {
    padding: 20px;
  }

  .video-info-section-content {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: start;
    gap: 12px;
    margin-top: 0px;
    padding: 0 20px;
  }

  .video-info-section-content-mentor {
    width: 100%;
    min-width: 335px;
    max-width: 560px;
    display: flex;
    flex: none;
    flex-direction: column;
    align-items: start;
    padding: 20px 12px;
    border-radius: 8px;
    background-color: var(--gray-800);
    min-height: auto;
    max-height: none;
  }

  .video-info-section-content-mentor-avatar {
    margin-top: 16px;
  }

  .video-info-section-content-description {
    width: 100%;
    min-width: 335px;
    max-width: 560px;
    padding: 20px 12px;
    min-height: auto;
    max-height: none;
  }

  .video-info-section-content-description-text {
    margin-top: 8px;
  }

  .video-info-section-content-description-info {
    margin-top: 20px;
    gap: 8px;
  }
}
</style>
